import { getRandomId } from '../../../../../common/utils/utils';
import { NODE_COLOR_PALETTE } from '../../../../labelling/constants/template-builder';
import type { StageEventDetailT } from '../../../../labelling/providers/core-stage-provider/types';
import type { KeypointItemT, NodeWithPositionT, SkeletonTemplateNodeT } from '../../../../labelling/types/annotations.type';
import type { TemplateNodeT } from '../../../types/skeleton-template-builder.type';
import { DATA_ATTR_NODE_ID } from '../constants';
export function generateNodeId(): string {
  return `kp_${getRandomId()}`;
}
export function getNodeIdFromTarget(target: Element | undefined): string | null {
  if (!target) return null;
  const el = target.closest(`[${DATA_ATTR_NODE_ID}]`);
  return el?.getAttribute(DATA_ATTR_NODE_ID) ?? null;
}
export function getStageEventDetail(event: Event): StageEventDetailT {
  return (event as CustomEvent<StageEventDetailT>).detail;
}
export function toPixel(xNorm: number, yNorm: number, width: number, height: number): [number, number] {
  return [xNorm * width, yNorm * height];
}

/**
 * Computes the largest axis-aligned box with the given aspect ratio that fits
 * inside the container, centred and inset by `padding` on all sides.
 */
export function computeContainFitBox(containerWidth: number, containerHeight: number, aspectRatio: number, padding: number): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const availableW = containerWidth - padding * 2;
  const availableH = containerHeight - padding * 2;
  const fitByWidth = availableW / aspectRatio <= availableH;
  const width = fitByWidth ? availableW : availableH * aspectRatio;
  const height = fitByWidth ? availableW / aspectRatio : availableH;
  return {
    x: (containerWidth - width) / 2,
    y: (containerHeight - height) / 2,
    width,
    height
  };
}
export function hexToLightShade(hex: string, opacity = 0.3): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Compute the tight bounding box around all skeleton keypoints.
 *
 * Users place keypoints on an arbitrary canvas, so the raw positions are
 * scattered across that coordinate space. The tight bounding box strips away
 * the surrounding whitespace so templates are stored in a canvas-independent,
 * compact form.
 */
function computeTightBoundingBox(nodes: ReadonlyArray<NodeWithPositionT>) {
  const xValues = nodes.map(n => n.x);
  const yValues = nodes.map(n => n.y);
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);
  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY
  };
}
export function computeBoundingBoxAspectRatio(nodes: ReadonlyArray<NodeWithPositionT>): number {
  if (nodes.length < 2) return 1;
  const {
    width,
    height
  } = computeTightBoundingBox(nodes);
  return height > 0 ? width / height : 1;
}
export function normalizeToTightBoundingBox(nodes: ReadonlyArray<SkeletonTemplateNodeT>): SkeletonTemplateNodeT[] {
  if (nodes.length < 2) return nodes.map(n => ({
    ...n
  }));
  const {
    minX,
    minY,
    width,
    height
  } = computeTightBoundingBox(nodes);
  return nodes.map(n => ({
    ...n,
    x: width > 0 ? (n.x - minX) / width : 0,
    y: height > 0 ? (n.y - minY) / height : 0
  }));
}
export function toTemplateNodes(nodes: SkeletonTemplateNodeT[]): TemplateNodeT[] {
  return nodes.map(node => ({
    ...node,
    color: node.color ?? NODE_COLOR_PALETTE[0]
  }));
}
export function nodesToKeypoints(nodes: TemplateNodeT[]): KeypointItemT[] {
  return nodes.map(n => ({
    id: n.id,
    label: n.label,
    color: n.color
  }));
}